import { z } from "zod";
import { ufSchema } from "./calculation.js";
import { nfeAccessKeySchema, nfeItemIssueSchema, nfeItemValuesSchema, nfeMoneySchema, nfeObservedPathSchema, nfePathStatusSchema, nfePeriodSchema, } from "./nfe-observations.js";
import { ruleProposalDraftSchema, ruleProposalDraftStatusSchema } from "./rule-drafts.js";
/**
 * Tax manager NF-e workspace ("Notas fiscais").
 *
 * Tax managers upload their company's NF-e, see them organised by period
 * (year → month, from `dhEmi` in America/Sao_Paulo), validate each month and
 * read findings. Deterministic findings are always on; AI findings are
 * optional, aggregated and minimised, stored apart (`source: "AI"`) and never
 * applied: they are suggestions that need review.
 *
 * Direction is derived, never stored: EMITIDA when the issuer's CNPJ root is
 * the company's own; RECEBIDA when the recipient is the company (the
 * importer keeps the recipient CNPJ only in that case). Invoices that are
 * neither are not part of the workspace (they stay as corroboration in the
 * observed paths).
 *
 * Permissions (no new permission: the Tax manager role already holds them):
 * - read (periods, paths, findings, events, runs, settings): READ:TAX_RULE:ALL
 * - upload: CREATE:TAX_NEWS:ALL (the existing importer, `POST /v1/nfe-imports`)
 * - acknowledge, validate, run analysis: REVIEW:TAX_RULE:ALL
 * - converting to a draft proposal additionally needs CREATE:TAX_RULE:ALL
 * - edit AI settings (kill switch, budget): ADMIN:TAX_PLATFORM:ALL
 */
export const NFE_WORKSPACE_TIMEZONE = "America/Sao_Paulo";
/** Own company recipients/issuers only; everything else is outside the workspace. */
export const nfeDirectionSchema = z.enum(["EMITIDA", "RECEBIDA"]);
export const NFE_DIRECTIONS = nfeDirectionSchema.options;
export const nfePeriodStatusSchema = z.enum(["NOT_REVIEWED", "IN_REVIEW", "VALIDATED"]);
export const NFE_PERIOD_STATUSES = nfePeriodStatusSchema.options;
export const nfeFindingSeveritySchema = z.enum(["HIGH", "MEDIUM", "LOW"]);
export const NFE_FINDING_SEVERITIES = nfeFindingSeveritySchema.options;
/** `RULES`: deterministic engine (always on). `AI`: model suggestions that need review. */
export const nfeFindingSourceSchema = z.enum(["RULES", "AI"]);
/** Deterministic checks. Each code is documented in docs/runbooks/nfe-tax-manager-workspace.md. */
export const nfeFindingCodeSchema = z.enum([
    /** CST 00/20 with a base but pICMS = 0 on a sale CFOP. */
    "ICMS_ZERO_RATE_ON_TAXED_CST",
    /** CFOP 6101/6102 to a non-contributor or final consumer without DIFAL fields. */
    "DIFAL_MISSING_NON_CONTRIBUTOR",
    /** Interstate pICMS inconsistent with the origin (4 %) or the route (7 %/12 %). */
    "INTERSTATE_RATE_INCONSISTENT",
    /** x403/x405 CFOP without the matching ST group. */
    "ST_CFOP_WITHOUT_ST_GROUP",
    /** An ST CST (10/30/70, CSOSN 201/202/203) without ST values. */
    "ST_CST_WITHOUT_ST_VALUES",
    /** CST 20/70 without pRedBC. */
    "REDUCTION_WITHOUT_PRED_BC",
    /** pRedBC stated but the CST is not a reduction CST. */
    "PRED_BC_WITHOUT_REDUCTION_CST",
    /** vICMS ≠ vBC × pICMS beyond rounding. */
    "ICMS_VALUE_MISMATCH",
    /** ST base ≠ (vProd + IPI + frete + seg + outros − desc) × (1 + MVA). */
    "ICMS_ST_BASE_MISMATCH",
    /** ST value ≠ ST base × rate − own ICMS. */
    "ICMS_ST_VALUE_MISMATCH",
    /** FCP, DIFAL, IPI, PIS, COFINS or IBS/CBS value ≠ base × rate. */
    "OTHER_TAX_VALUE_MISMATCH",
    /** PIS/COFINS CST 01/02 at 0 %. */
    "PIS_COFINS_ZERO_RATE_CST01",
    /** Monophasic NCM resold with PIS/COFINS CST 01. */
    "MONOPHASIC_CHARGED_AS_CST01",
    /** An emitted invoice that repeats the items and totals of another within 30 days. */
    "POSSIBLE_DUPLICATE_EMITTED",
    /** The path diverges from what the platform holds (rule, fact or IOB). */
    "PLATFORM_RULE_DIVERGENCE",
]);
export const NFE_FINDING_CODES = nfeFindingCodeSchema.options;
export const nfeAiFindingKindSchema = z.enum([
    "CANDIDATE_RULE",
    "SUSPECTED_ISSUER_ERROR",
    "COMPLIANCE_RISK",
    "RESEARCH_SUGGESTION",
]);
export const nfeFindingStatusSchema = z.enum([
    "OPEN",
    /** A deterministic finding the latest run no longer reproduces (data changed). */
    "RESOLVED",
    /** An AI finding replaced by a newer completed AI run of the month. */
    "SUPERSEDED",
]);
export const nfeResolutionSchema = z.enum([
    "ACCEPTED_ISSUER_ERROR",
    "ACCEPTED_CORRECT",
    "CONVERTED_TO_DRAFT",
    /** AI suggestions only: reviewed and not pursued. */
    "DISMISSED",
]);
export const NFE_ACK_REASON_MIN = 10;
export const NFE_ACK_REASON_MAX = 1_000;
const reasonSchema = z.string().trim().min(NFE_ACK_REASON_MIN).max(NFE_ACK_REASON_MAX);
/** Money in reais, two places (`1234.50`). */
const reaisSchema = nfeMoneySchema;
const rootSchema = z.string().regex(/^\d{8}$/);
const usdSchema = z.string().regex(/^(0|[1-9]\d{0,7})(\.\d{1,6})?$/);
/* -- Statistics ---------------------------------------------------------------- */
export const nfePeriodStatsSchema = z
    .object({
    invoices: z.number().int().nonnegative(),
    items: z.number().int().nonnegative(),
    /** Invoices cancelled (protocol or event) and therefore left out of everything else. */
    cancelled: z.number().int().nonnegative(),
    /** Σ (vProd + vFrete + vSeg + vOutro − vDesc) of the active items, in reais. */
    totalValue: reaisSchema,
    observedPaths: z.number().int().nonnegative(),
    comparison: z
        .object({
        MATCHES: z.number().int().nonnegative(),
        DIVERGES: z.number().int().nonnegative(),
        NOT_COVERED: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
export const nfeFindingCountsSchema = z
    .object({
    HIGH: z.number().int().nonnegative(),
    MEDIUM: z.number().int().nonnegative(),
    LOW: z.number().int().nonnegative(),
    /** Open HIGH deterministic findings that have no acknowledgement yet. */
    openHigh: z.number().int().nonnegative(),
    /** Open AI suggestions (never block validation). */
    ai: z.number().int().nonnegative(),
})
    .strict();
export const nfePeriodNoticeSchema = z
    .object({
    /** A new upload landed in a validated month: it went back to review. */
    code: z.literal("REOPENED_BY_UPLOAD"),
    batchId: z.string().uuid(),
    at: z.string().datetime(),
    /** Invoices the upload added (or cancelled/replaced) in the month. */
    changedInvoices: z.number().int().positive(),
})
    .strict();
export const nfePeriodSummarySchema = z
    .object({
    period: nfePeriodSchema,
    status: nfePeriodStatusSchema,
    /** Optimistic concurrency (`If-Match`) of the validation state. */
    revision: z.number().int().nonnegative(),
    /** Under the direction filter of the request. */
    stats: nfePeriodStatsSchema,
    findings: nfeFindingCountsSchema,
    notice: nfePeriodNoticeSchema.nullable(),
    validatedAt: z.string().datetime().nullable(),
    validatedBy: z.string().uuid().nullable(),
    updatedAt: z.string().datetime().nullable(),
})
    .strict();
export const nfeAiAvailabilityReasonSchema = z.enum([
    /** The deployment has no AI lane (env flag off). */
    "NOT_DEPLOYED",
    "ARGON_COST_BOUND_UNAVAILABLE",
    /** An administrator switched the lane off in the workspace settings. */
    "SWITCHED_OFF",
    "MONTHLY_BUDGET_REACHED",
    "DAILY_CAP_REACHED",
]);
export const nfeAiAvailabilitySchema = z
    .object({
    available: z.boolean(),
    reasons: z.array(nfeAiAvailabilityReasonSchema).max(5),
    autoRunAfterImport: z.boolean(),
})
    .strict();
/** `GET /v1/nfe-workspace/periods`. */
export const nfePeriodTreeSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    direction: nfeDirectionSchema.nullable(),
    years: z
        .array(z
        .object({
        year: z.number().int().min(2000).max(2100),
        months: z.array(nfePeriodSummarySchema).min(1).max(12),
    })
        .strict())
        .max(60),
    /** Invoices on file that are neither issued nor received by the company. */
    outsideWorkspaceInvoices: z.number().int().nonnegative(),
    ai: nfeAiAvailabilitySchema,
    /** More paths than one comparison scan reads: status counts are partial. */
    truncated: z.boolean(),
})
    .strict();
/** What still blocks `VALIDATE` for the month (both directions, never the filter). */
export const nfeReadinessSchema = z
    .object({
    canValidate: z.boolean(),
    divergingPaths: z.number().int().nonnegative(),
    unacknowledgedDivergingPaths: z.number().int().nonnegative(),
    highFindings: z.number().int().nonnegative(),
    unacknowledgedHighFindings: z.number().int().nonnegative(),
})
    .strict();
/** `GET /v1/nfe-workspace/periods/{period}`. */
export const nfePeriodDetailSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    direction: nfeDirectionSchema.nullable(),
    summary: nfePeriodSummarySchema,
    /** The month by direction, whatever the filter. */
    byDirection: z
        .object({ EMITIDA: nfePeriodStatsSchema, RECEBIDA: nfePeriodStatsSchema })
        .strict(),
    readiness: nfeReadinessSchema,
    ai: nfeAiAvailabilitySchema,
    lastRulesRunAt: z.string().datetime().nullable(),
    lastAiRun: z.lazy(() => nfeAnalysisRunSchema).nullable(),
    truncated: z.boolean(),
})
    .strict();
/* -- Paths in a month ------------------------------------------------------------ */
export const nfeAcknowledgementSummarySchema = z
    .object({
    id: z.string().uuid(),
    resolution: nfeResolutionSchema,
    reason: z.string(),
    createdBy: z.string().uuid(),
    createdByLabel: z.string().nullable(),
    createdAt: z.string().datetime(),
    draftId: z.string().uuid().nullable(),
    /** False when the divergence changed after the acknowledgement was recorded. */
    current: z.boolean(),
})
    .strict();
/** An observed path restricted to the month (and direction) it is read in. */
export const nfeMonthPathSchema = nfeObservedPathSchema
    .extend({
    acknowledgement: nfeAcknowledgementSummarySchema.nullable(),
})
    .strict();
export const nfeMonthPathQuerySchema = z
    .object({
    direction: nfeDirectionSchema.optional(),
    statuses: z.array(nfePathStatusSchema).min(1).max(3).optional(),
    ncmPrefix: z
        .string()
        .regex(/^\d{2,8}$/)
        .optional(),
    acknowledged: z.boolean().optional(),
})
    .strict();
export const nfeMonthPathPageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    period: nfePeriodSchema,
    items: z.array(nfeMonthPathSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
    statusCounts: z
        .object({
        MATCHES: z.number().int().nonnegative(),
        DIVERGES: z.number().int().nonnegative(),
        NOT_COVERED: z.number().int().nonnegative(),
    })
        .strict(),
    truncated: z.boolean(),
})
    .strict();
export const NFE_MONTH_SAMPLE_INVOICES = 20;
export const nfeMonthSampleInvoiceSchema = z
    .object({
    accessKey: nfeAccessKeySchema,
    issuedAt: z.string().datetime({ offset: true }),
    direction: nfeDirectionSchema,
    /** CNPJ root (8 digits) of a company issuer; null for a CPF (never stored). */
    emitterCnpjRoot: rootSchema.nullable(),
})
    .strict();
export const nfeMonthPathDetailSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    period: nfePeriodSchema,
    path: nfeMonthPathSchema,
    sampleInvoices: z.array(nfeMonthSampleInvoiceSchema).max(NFE_MONTH_SAMPLE_INVOICES),
    inconsistentItems: z
        .array(z
        .object({
        accessKey: nfeAccessKeySchema,
        itemNumber: z.number().int().min(1).max(990),
        issues: z.array(nfeItemIssueSchema).min(1).max(11),
        values: nfeItemValuesSchema,
    })
        .strict())
        .max(NFE_MONTH_SAMPLE_INVOICES),
    drafts: z
        .array(z
        .object({
        id: z.string().uuid(),
        status: ruleProposalDraftStatusSchema,
        targetRulesetVersion: z.string().min(1),
        proposalId: z.string().uuid().nullable(),
    })
        .strict())
        .max(20),
    /** Deterministic findings of the month that point at this path. */
    findingIds: z.array(z.string().uuid()).max(50),
})
    .strict();
/* -- Findings ------------------------------------------------------------------- */
const findingValueSchema = z.union([z.string().max(200), z.number(), z.boolean(), z.null()]);
export const nfeAiLegalBasisSchema = z
    .object({
    /** An act, article and clause as the model cites it. It is a pointer to verify, never evidence. */
    reference: z.string().trim().min(3).max(300),
    note: z.string().trim().max(500).nullable().optional(),
})
    .strict();
export const nfeAiCandidateRuleSchema = z
    .object({
    ncm: z.string().regex(/^\d{8}$/),
    cest: z
        .string()
        .regex(/^\d{2}\.\d{3}\.\d{2}$/)
        .nullable()
        .optional(),
    originUf: ufSchema,
    destinationUf: ufSchema,
    /** Free text the reviewer reads: what the model suggests the rule states. */
    suggestion: z.string().trim().min(3).max(600),
})
    .strict();
export const nfeFindingAiSchema = z
    .object({
    kind: nfeAiFindingKindSchema,
    runId: z.string().uuid(),
    model: z.string().min(1).max(100),
    promptVersion: z.string().min(1).max(60),
    /** 0 to 1, as the model states it. Calibrate with care: it is not a probability. */
    confidence: z.number().min(0).max(1),
    legalBasis: z.array(nfeAiLegalBasisSchema).max(5),
    candidateRule: nfeAiCandidateRuleSchema.nullable(),
    /** Observed paths of the month the suggestion refers to. */
    pathKeys: z.array(z.string().regex(/^[a-f0-9]{64}$/)).max(10),
    /** Deterministic findings of the month the suggestion refers to. */
    findingIds: z.array(z.string().uuid()).max(10),
    researchQuery: z.string().trim().max(300).nullable(),
})
    .strict();
export const nfeFindingSchema = z
    .object({
    id: z.string().uuid(),
    period: nfePeriodSchema,
    source: nfeFindingSourceSchema,
    /** Deterministic code, or `AI_<KIND>` for AI suggestions. */
    code: z.string().regex(/^[A-Z][A-Z0-9_]{2,59}$/),
    severity: nfeFindingSeveritySchema,
    status: nfeFindingStatusSchema,
    direction: nfeDirectionSchema.nullable(),
    title: z.string().max(200),
    /** Null for AI findings (aggregated, no invoice) and period-level checks. */
    accessKey: nfeAccessKeySchema.nullable(),
    /** Related invoice for a possible duplicate. */
    relatedAccessKey: nfeAccessKeySchema.nullable(),
    items: z.array(z.number().int().min(1).max(990)).max(50),
    itemCount: z.number().int().nonnegative(),
    pathKey: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable(),
    pathId: z.string().uuid().nullable(),
    /** Values behind the finding (bases, rates, stated and expected). */
    values: z.record(z.string(), findingValueSchema),
    /** Estimated tax at stake in reais; null when it cannot be estimated without inventing a rate. */
    valueAtStake: reaisSchema.nullable(),
    explanation: z.string().max(2_000),
    suggestedAction: z.string().max(1_000),
    ai: nfeFindingAiSchema.nullable(),
    acknowledgement: nfeAcknowledgementSummarySchema.nullable(),
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
})
    .strict();
export const nfeFindingQuerySchema = z
    .object({
    direction: nfeDirectionSchema.optional(),
    severities: z.array(nfeFindingSeveritySchema).min(1).max(3).optional(),
    source: nfeFindingSourceSchema.optional(),
    code: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{2,59}$/)
        .optional(),
    acknowledged: z.boolean().optional(),
    /** Default `OPEN`. */
    status: nfeFindingStatusSchema.optional(),
})
    .strict();
export const nfeFindingPageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    period: nfePeriodSchema,
    items: z.array(nfeFindingSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
    /** Under the direction/source filters (the severity filter aside). */
    severityCounts: z
        .object({
        HIGH: z.number().int().nonnegative(),
        MEDIUM: z.number().int().nonnegative(),
        LOW: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
/* -- Acknowledgements and validation -------------------------------------------- */
/**
 * `POST /v1/nfe-workspace/periods/{period}/acknowledgements`
 * (REVIEW:TAX_RULE:ALL, Idempotency-Key). Append-only: the latest
 * acknowledgement of a target counts. `CONVERTED_TO_DRAFT` on a path (or a
 * finding that points at one) creates or reuses the EVIDENCE_REQUIRED draft
 * of the observed-path flow (also needs CREATE:TAX_RULE:ALL); it never
 * approves or publishes anything.
 */
export const nfeAcknowledgementRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    target: z.discriminatedUnion("type", [
        z.object({ type: z.literal("FINDING"), findingId: z.string().uuid() }).strict(),
        z.object({ type: z.literal("PATH"), pathId: z.string().uuid() }).strict(),
    ]),
    resolution: nfeResolutionSchema,
    reason: reasonSchema,
})
    .strict();
export const nfeAcknowledgementResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    acknowledgement: nfeAcknowledgementSummarySchema,
    /** Present for CONVERTED_TO_DRAFT. */
    draft: ruleProposalDraftSchema.nullable(),
    summary: nfePeriodSummarySchema,
    readiness: nfeReadinessSchema,
})
    .strict();
export const nfeValidationActionSchema = z.enum(["START_REVIEW", "VALIDATE", "REOPEN"]);
/**
 * `POST /v1/nfe-workspace/periods/{period}/validation`
 * (REVIEW:TAX_RULE:ALL, `If-Match`, Idempotency-Key).
 * NOT_REVIEWED → IN_REVIEW → VALIDATED; REOPEN returns a VALIDATED month to
 * IN_REVIEW and needs a reason. VALIDATE answers 409 OPERATION_NOT_AVAILABLE
 * while a DIVERGES path or a HIGH finding of the month has no acknowledgement.
 */
export const nfeValidationRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    action: nfeValidationActionSchema,
    reason: reasonSchema.optional(),
})
    .strict()
    .refine((value) => value.action !== "REOPEN" || value.reason !== undefined, {
    message: "A reason is required to reopen a validated month",
    path: ["reason"],
});
export const nfeValidationResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    summary: nfePeriodSummarySchema,
    readiness: nfeReadinessSchema,
})
    .strict();
/* -- Audit trail ---------------------------------------------------------------- */
export const nfePeriodEventTypeSchema = z.enum([
    "REVIEW_STARTED",
    "VALIDATED",
    "REOPENED",
    "REOPENED_BY_UPLOAD",
    "ACKNOWLEDGED",
    "RULES_ANALYSIS_RUN",
    "AI_ANALYSIS_REQUESTED",
    "AI_ANALYSIS_COMPLETED",
]);
export const nfePeriodEventSchema = z
    .object({
    id: z.string().uuid(),
    period: nfePeriodSchema,
    type: nfePeriodEventTypeSchema,
    /** Operator, or null for the system (an upload reopening a month). */
    actorId: z.string().uuid().nullable(),
    actorLabel: z.string().nullable(),
    reason: z.string().nullable(),
    at: z.string().datetime(),
    metadata: z.record(z.string(), findingValueSchema),
})
    .strict();
export const nfePeriodEventPageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    period: nfePeriodSchema,
    items: z.array(nfePeriodEventSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
/* -- Analysis runs --------------------------------------------------------------- */
export const nfeAnalysisKindSchema = z.enum(["RULES", "AI"]);
export const nfeAnalysisStatusSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "COMPLETED",
    "FAILED",
    "CANCELLED",
]);
export const nfeAnalysisTriggerSchema = z.enum(["IMPORT", "MANUAL", "AUTO_AFTER_IMPORT"]);
export const nfeAnalysisRunSchema = z
    .object({
    id: z.string().uuid(),
    period: nfePeriodSchema,
    kind: nfeAnalysisKindSchema,
    status: nfeAnalysisStatusSchema,
    trigger: nfeAnalysisTriggerSchema,
    counts: z
        .object({
        findings: z.number().int().nonnegative(),
        HIGH: z.number().int().nonnegative(),
        MEDIUM: z.number().int().nonnegative(),
        LOW: z.number().int().nonnegative(),
        /** Deterministic findings the run no longer reproduced. */
        resolved: z.number().int().nonnegative(),
    })
        .strict(),
    model: z.string().nullable(),
    promptVersion: z.string().nullable(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costUsd: usdSchema,
    errorCode: z.string().nullable(),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
})
    .strict();
/**
 * `POST /v1/nfe-workspace/periods/{period}/analysis` (REVIEW:TAX_RULE:ALL,
 * Idempotency-Key). `RULES` runs the deterministic engine now (200).
 * `AI` queues one AI run (202) when the lane is available: 409
 * OPERATION_NOT_AVAILABLE with the reasons otherwise.
 */
export const nfeAnalysisRequestSchema = z
    .object({ schemaVersion: z.literal("1.0.0"), kind: nfeAnalysisKindSchema })
    .strict();
export const nfeAnalysisResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    run: nfeAnalysisRunSchema,
    summary: nfePeriodSummarySchema,
    readiness: nfeReadinessSchema,
})
    .strict();
export const nfeAnalysisRunPageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    period: nfePeriodSchema,
    items: z.array(nfeAnalysisRunSchema).max(100),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
/* -- Settings -------------------------------------------------------------------- */
export const nfeAiPriceTableSchema = z
    .object({
    inputTokensUsdPerMillion: usdSchema,
    outputTokensUsdPerMillion: usdSchema,
})
    .strict();
const nfeSettingsFields = {
    /** Kill switch. Off by default: nothing is queued and a claim returns no work. */
    aiEnabled: z.boolean(),
    /** Queue one AI run per affected month after each import (when the lane is available). */
    aiAutoRunAfterImport: z.boolean(),
    monthlyBudgetUsd: usdSchema,
    dailyRunCap: z.number().int().min(1).max(1_000),
    priceTable: nfeAiPriceTableSchema,
};
export const nfeWorkspaceSettingsUpdateSchema = z
    .object({ schemaVersion: z.literal("1.0.0"), ...nfeSettingsFields })
    .strict();
export const NFE_AI_DEFAULT_SETTINGS = {
    aiEnabled: false,
    aiAutoRunAfterImport: false,
    monthlyBudgetUsd: "20",
    dailyRunCap: 40,
    priceTable: { inputTokensUsdPerMillion: "4", outputTokensUsdPerMillion: "20" },
};
/** `GET/PUT /v1/nfe-workspace/settings` (read: READ:TAX_RULE:ALL, write: ADMIN:TAX_PLATFORM:ALL, `If-Match`). */
export const nfeWorkspaceSettingsSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    ...nfeSettingsFields,
    /** The deployment carries the AI lane (ENABLE_NFE_AI_ANALYSIS). */
    aiDeployed: z.boolean(),
    availability: nfeAiAvailabilitySchema,
    spentThisMonthUsd: usdSchema,
    runsToday: z.number().int().nonnegative(),
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().uuid().nullable(),
    revision: z.number().int().nonnegative(),
})
    .strict();
/* -- AI: minimised payload, model output and the worker lane --------------------- */
export const NFE_AI_PROMPT_VERSION = "nfe-month-review/1";
export const NFE_AI_MAX_PATHS = 80;
export const NFE_AI_MAX_FINDING_GROUPS = 60;
export const NFE_AI_MAX_FINDINGS = 20;
/**
 * The only data a model ever sees: one month's aggregated observed paths,
 * the deterministic findings aggregated by code/path and counts. No access
 * key, no item, no invoice value per invoice, no raw XML, no personal data;
 * emitter CNPJ roots (companies) only. `ref`s (`P1`, `F1`) are how the model
 * points back; the API resolves them.
 */
export const nfeAiPayloadSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    promptVersion: z.string().min(1).max(60),
    period: nfePeriodSchema,
    direction: nfeDirectionSchema.nullable(),
    totals: z
        .object({
        invoices: z.number().int().nonnegative(),
        items: z.number().int().nonnegative(),
        cancelled: z.number().int().nonnegative(),
        totalValue: reaisSchema,
        observedPaths: z.number().int().nonnegative(),
    })
        .strict(),
    paths: z
        .array(z
        .object({
        ref: z.string().regex(/^P\d{1,3}$/),
        direction: z.union([nfeDirectionSchema, z.literal("BOTH")]),
        originUf: ufSchema,
        destinationUf: ufSchema,
        ncm: z.string().regex(/^\d{8}$/),
        cest: z.string().nullable(),
        cfop: z.string().regex(/^[1-7]\d{3}$/),
        operation: z.record(z.string(), findingValueSchema),
        treatment: z.record(z.string(), z.unknown()),
        counts: z
            .object({
            items: z.number().int().positive(),
            invoices: z.number().int().positive(),
            emitters: z.number().int().nonnegative(),
        })
            .strict(),
        /** CNPJ roots of up to five company issuers. */
        emitterRoots: z.array(rootSchema).max(5),
        comparison: z
            .object({
            status: nfePathStatusSchema,
            divergences: z
                .array(z
                .object({
                field: z.string(),
                expected: findingValueSchema,
                observed: findingValueSchema,
                source: z.string(),
            })
                .strict())
                .max(5),
        })
            .strict(),
    })
        .strict())
        .max(NFE_AI_MAX_PATHS),
    pathsOmitted: z.number().int().nonnegative(),
    findings: z
        .array(z
        .object({
        ref: z.string().regex(/^F\d{1,3}$/),
        code: z.string(),
        severity: nfeFindingSeveritySchema,
        direction: nfeDirectionSchema.nullable(),
        invoices: z.number().int().nonnegative(),
        items: z.number().int().nonnegative(),
        valueAtStake: reaisSchema.nullable(),
        pathRef: z
            .string()
            .regex(/^P\d{1,3}$/)
            .nullable(),
        acknowledged: z.boolean(),
    })
        .strict())
        .max(NFE_AI_MAX_FINDING_GROUPS),
})
    .strict();
/** One finding as the model returns it (the worker validates, the API validates again). */
export const nfeAiModelFindingSchema = z
    .object({
    kind: nfeAiFindingKindSchema,
    severity: nfeFindingSeveritySchema,
    title: z.string().trim().min(3).max(160),
    explanation: z.string().trim().min(10).max(1_200),
    suggestedAction: z.string().trim().min(3).max(600),
    confidence: z.number().min(0).max(1),
    legalBasis: z.array(nfeAiLegalBasisSchema).max(5),
    candidateRule: nfeAiCandidateRuleSchema.nullable(),
    pathRefs: z.array(z.string().regex(/^P\d{1,3}$/)).max(10),
    findingRefs: z.array(z.string().regex(/^F\d{1,3}$/)).max(10),
    researchQuery: z.string().trim().max(300).nullable(),
})
    .strict()
    .refine((value) => value.kind !== "CANDIDATE_RULE" || (value.legalBasis.length > 0 && value.candidateRule), {
    message: "A candidate rule needs a legal basis and the route it concerns",
    path: ["legalBasis"],
});
export const nfeAiModelOutputSchema = z
    .object({ findings: z.array(nfeAiModelFindingSchema).max(NFE_AI_MAX_FINDINGS) })
    .strict();
/** Runtime lane: the worker claims one queued AI run (NFE_ANALYSIS_AGENT, Google ID token). */
export const nfeAnalysisClaimRequestSchema = z
    .object({ schemaVersion: z.literal("1.0.0"), environment: z.enum(["dev", "stage", "prod"]) })
    .strict();
export const nfeAnalysisClaimSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    /** Why nothing was handed out (kill switch, budget, empty queue). */
    stopReason: z.enum(["NONE", "SWITCHED_OFF", "NOT_DEPLOYED", "BUDGET_REACHED", "EMPTY"]),
    run: z
        .object({
        id: z.string().uuid(),
        period: nfePeriodSchema,
        leaseToken: z.string().uuid(),
        leaseExpiresAt: z.string().datetime(),
        attempts: z.number().int().positive(),
        payload: nfeAiPayloadSchema,
    })
        .strict()
        .nullable(),
})
    .strict();
export const nfeAnalysisResultReportSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    leaseToken: z.string().uuid(),
    model: z.string().min(1).max(100),
    promptVersion: z.string().min(1).max(60),
    usage: z
        .object({
        inputTokens: z.number().int().nonnegative().max(10_000_000),
        outputTokens: z.number().int().nonnegative().max(10_000_000),
    })
        .strict(),
    output: nfeAiModelOutputSchema,
})
    .strict();
export const nfeAnalysisReleaseSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    leaseToken: z.string().uuid(),
    error: z
        .object({
        code: z.string().regex(/^[A-Z][A-Z0-9_]{2,59}$/),
        retryable: z.boolean(),
    })
        .strict(),
    usage: z
        .object({
        inputTokens: z.number().int().nonnegative().max(10_000_000),
        outputTokens: z.number().int().nonnegative().max(10_000_000),
    })
        .strict()
        .optional(),
    model: z.string().min(1).max(100).optional(),
})
    .strict();
/** Outcome of a result or release call. */
export const nfeAnalysisReceiptSchema = z
    .object({ schemaVersion: z.literal("1.0.0"), run: nfeAnalysisRunSchema })
    .strict();
//# sourceMappingURL=nfe-workspace.js.map